/**
 * CelebrationModal - Full-screen notification modal with gradient background
 * Based on Figma designs with 7 different theme options
 */

import { useEffect, useRef, useCallback } from "react";
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  Dimensions,
  StyleSheet,
  Animated,
  StatusBar,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import LottieView from "lottie-react-native";
import { Ionicons } from "@expo/vector-icons";
import { router } from "expo-router";
import { CelebrationModalProps, InAppNotification, NotificationTheme } from "../types";
import { getLottieAnimation } from "../utils/lottieAnimations";
import { getThemeById } from "../utils/themes";

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get("window");

/**
 * CelebrationModal component
 * Displays a full-screen celebration/notification overlay
 */
export const CelebrationModal = ({
  visible,
  onDismiss,
  notification,
  theme: propTheme,
}: CelebrationModalProps) => {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.8)).current;
  const lottieRef = useRef<LottieView>(null);

  // Get theme from prop or notification's action_data or default
  const getNotificationThemeId = (): string | null => {
    if (!notification) return null;
    // theme_id is stored in action_data (from createScoreNotifications)
    const actionData = notification.action_data as Record<string, unknown> | null;
    if (actionData?.theme_id && typeof actionData.theme_id === "string") {
      return actionData.theme_id;
    }
    return null;
  };
  
  const theme: NotificationTheme = propTheme || 
    getThemeById(getNotificationThemeId()) || 
    getThemeById(null);

  /**
   * Animate in when visible
   */
  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.spring(scaleAnim, {
          toValue: 1,
          friction: 8,
          tension: 40,
          useNativeDriver: true,
        }),
      ]).start();

      // Play lottie animation
      setTimeout(() => {
        lottieRef.current?.play();
      }, 100);
    } else {
      fadeAnim.setValue(0);
      scaleAnim.setValue(0.8);
    }
  }, [visible, fadeAnim, scaleAnim]);

  /**
   * Handle dismiss with animation
   */
  const handleDismiss = useCallback(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 0.8,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start(() => {
      onDismiss();
    });
  }, [fadeAnim, scaleAnim, onDismiss]);

  /**
   * Get gradient colors from theme
   */
  const getGradientColors = (): [string, string, ...string[]] => {
    if (theme.gradient_middle) {
      return [theme.gradient_start, theme.gradient_middle, theme.gradient_end];
    }
    return [theme.gradient_start, theme.gradient_end];
  };

  /**
   * Get title text (with template variable replacement)
   */
  const getTitle = (): string => {
    if (!notification) return "Notificatie";
    
    // Check for custom title in action_data
    const actionData = notification.action_data as Record<string, unknown> | null;
    if (actionData?.custom_title) {
      return String(actionData.custom_title);
    }
    
    return notification.title || "Notificatie";
  };

  /**
   * Get body text (with template variable replacement)
   */
  const getBody = (): string => {
    if (!notification) return "";
    
    // Check for custom body in action_data
    const actionData = notification.action_data as Record<string, unknown> | null;
    if (actionData?.custom_body) {
      return String(actionData.custom_body);
    }
    
    return notification.body || "";
  };

  /**
   * Get points from notification
   */
  const getPoints = (): number | null => {
    if (!notification?.action_data) return null;
    const actionData = notification.action_data as Record<string, unknown>;
    if (typeof actionData.points === "number") {
      return actionData.points;
    }
    return null;
  };

  /**
   * Render points badge if applicable
   */
  const renderPointsBadge = () => {
    const points = getPoints();
    if (points === null) return null;

    const isNegative = points < 0;
    const displayPoints = Math.abs(points);

    return (
      <View style={styles.pointsBadgeContainer}>
        <View style={[styles.pointsBadge, isNegative && styles.pointsBadgeNegative]}>
          <Text style={styles.pointsText}>
            {isNegative ? "-" : "+"}{displayPoints}
          </Text>
          <Text style={styles.pointsLabel}>punten</Text>
        </View>
      </View>
    );
  };

  /**
   * Get lottie animation source
   */
  const getLottieSource = () => {
    return getLottieAnimation(notification?.lottie_animation);
  };

  /**
   * Get action URL from notification
   */
  const getActionUrl = (): string | null => {
    if (notification?.action_url) return notification.action_url;
    const actionData = notification?.action_data as Record<string, unknown> | null;
    if (actionData?.action_url) return String(actionData.action_url);
    return null;
  };

  /**
   * Get action button text
   */
  const getActionButtonText = (): string => {
    const actionData = notification?.action_data as Record<string, unknown> | null;
    if (actionData?.action_button_text) return String(actionData.action_button_text);
    
    // Default button texts based on notification type
    if (notification?.notification_type === "game_challenge") return "Naar het spel";
    if (notification?.type === "game_challenge") return "Naar het spel";
    return "Bekijken";
  };

  /**
   * Handle action button press
   */
  const handleAction = useCallback(() => {
    const url = getActionUrl();
    if (url) {
      handleDismiss();
      // Small delay to allow dismiss animation
      setTimeout(() => {
        router.push(url as any);
      }, 250);
    }
  }, [handleDismiss]);

  if (!visible || !notification) return null;

  const actionUrl = getActionUrl();

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      statusBarTranslucent
      onRequestClose={handleDismiss}
    >
      <StatusBar backgroundColor="transparent" barStyle="light-content" />
      <Animated.View style={[styles.container, { opacity: fadeAnim }]}>
        <LinearGradient
          colors={getGradientColors()}
          style={styles.gradient}
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
        >
          {/* Corner decorations */}
          <View style={[styles.cornerCircle, styles.topLeftCircle, { backgroundColor: theme.corner_color_1 + "40" }]} />
          <View style={[styles.cornerCircle, styles.topRightCircle, { backgroundColor: theme.corner_color_2 + "40" }]} />
          <View style={[styles.cornerCircle, styles.bottomLeftCircle, { backgroundColor: theme.corner_color_2 + "40" }]} />
          <View style={[styles.cornerCircle, styles.bottomRightCircle, { backgroundColor: theme.corner_color_1 + "40" }]} />

          {/* Close button */}
          <TouchableOpacity
            style={styles.closeButton}
            onPress={handleDismiss}
            accessibilityLabel="Sluiten"
            accessibilityRole="button"
          >
            <Ionicons name="close" size={28} color="white" />
          </TouchableOpacity>

          {/* Content */}
          <Animated.View
            style={[
              styles.content,
              {
                transform: [{ scale: scaleAnim }],
              },
            ]}
          >
            {/* Lottie Animation */}
            <View style={styles.lottieContainer}>
              <LottieView
                ref={lottieRef}
                source={getLottieSource()}
                style={styles.lottie}
                loop
                autoPlay={false}
              />
            </View>

            {/* Title */}
            <Text style={styles.title}>{getTitle()}</Text>

            {/* Points Badge */}
            {renderPointsBadge()}

            {/* Body */}
            <Text style={styles.body}>{getBody()}</Text>

            {/* Action Button */}
            {actionUrl && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleAction}
                accessibilityLabel={getActionButtonText()}
                accessibilityRole="button"
              >
                <Ionicons name="arrow-forward" size={20} color="#FFFFFF" style={{ marginRight: 8 }} />
                <Text style={styles.actionButtonText}>{getActionButtonText()}</Text>
              </TouchableOpacity>
            )}
          </Animated.View>

          {/* Tap to dismiss hint */}
          <TouchableOpacity 
            style={styles.dismissArea} 
            onPress={handleDismiss}
            activeOpacity={1}
            accessibilityLabel="Tik om te sluiten"
          >
            <Text style={styles.dismissHint}>Tik om te sluiten</Text>
          </TouchableOpacity>
        </LinearGradient>
      </Animated.View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  gradient: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  },
  closeButton: {
    position: "absolute",
    top: 60,
    right: 24,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
  },
  content: {
    alignItems: "center",
    paddingHorizontal: 32,
    maxWidth: 400,
  },
  lottieContainer: {
    width: 200,
    height: 200,
    marginBottom: 24,
  },
  lottie: {
    width: "100%",
    height: "100%",
  },
  title: {
    fontSize: 36,
    fontWeight: "800",
    color: "white",
    textAlign: "center",
    marginBottom: 16,
    textShadowColor: "rgba(0, 0, 0, 0.2)",
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  body: {
    fontSize: 18,
    color: "rgba(255, 255, 255, 0.9)",
    textAlign: "center",
    lineHeight: 26,
    marginTop: 16,
  },
  pointsBadgeContainer: {
    marginVertical: 8,
  },
  pointsBadge: {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    borderRadius: 24,
    paddingVertical: 16,
    paddingHorizontal: 40,
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 8,
  },
  pointsBadgeNegative: {
    backgroundColor: "rgba(239, 68, 68, 0.95)",
  },
  pointsText: {
    fontSize: 48,
    fontWeight: "900",
    color: "#7c3aed",
  },
  pointsLabel: {
    fontSize: 16,
    fontWeight: "600",
    color: "#6b7280",
    marginTop: -4,
  },
  dismissArea: {
    position: "absolute",
    bottom: 60,
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  dismissHint: {
    fontSize: 14,
    color: "rgba(255, 255, 255, 0.7)",
    textAlign: "center",
  },
  actionButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.25)",
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 16,
    marginTop: 24,
    borderWidth: 2,
    borderColor: "rgba(255, 255, 255, 0.4)",
  },
  actionButtonText: {
    fontSize: 18,
    fontWeight: "700",
    color: "#FFFFFF",
  },
  // Corner decoration circles
  cornerCircle: {
    position: "absolute",
    width: 200,
    height: 200,
    borderRadius: 100,
  },
  topLeftCircle: {
    top: -60,
    left: -60,
  },
  topRightCircle: {
    top: -40,
    right: -80,
  },
  bottomLeftCircle: {
    bottom: -80,
    left: -60,
  },
  bottomRightCircle: {
    bottom: -60,
    right: -80,
  },
});

export default CelebrationModal;


